import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { VTableRowStatus } from '@app/directives/_layout/v-table/_enums/VTableRowStatus.enum';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { ResourceType } from '@app/enum/ResourceType';
import { SortDirection } from '@app/enum/SortDirection';
import { IGoogleMapsMarker } from '@app/interfaces/IGoogleMapsMarkerOptions';
import { IJourney } from '@app/interfaces/IJourney';
import { IUnit, IViewCommissionPeripheral } from '@app/interfaces/IPeripheral';
import { IPlot } from '@app/interfaces/IPlot';
import { ISensorData } from '@app/interfaces/ISensorData';
import { SensorDataValueType } from '@app/interfaces/ISensorDataValue';
import { IVehicle } from '@app/interfaces/IVehicle';
import { Diagnostics4gComponent } from '@app/main/modules/foundation/_components/diagnostics4G/diagnostics-4g.component';
import { AddVehicleComponent } from '@app/main/modules/foundation/_routes/commissions/commissionEditor/components/add-vehicle/add-vehicle.component';
import { PulseService } from '@app/main/modules/foundation/_routes/hardware/peripherals/pulse/pulse.service';
import { IPulseRecord } from '@app/main/modules/foundation/_routes/hardware/_models/IPulseRecord';
import { DateService } from '@app/services/date.service';
import { JourneyService } from '@app/services/journey.service';
import { MapService } from '@app/services/map.service';
import { NavigationService } from '@app/services/navigation.service';
import { PlotService } from '@app/services/plots/plot.service';
import { ReasonCodeService } from '@app/services/reasonCode.service';
import { TranslationService } from '@app/services/translation.service';
import { UnitConversionService, UnitConversionType } from '@app/services/unitConversion.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, from, Observable } from 'rxjs';
import { PeripheralService } from '../../../_services/peripheral.service';
import { UnitsService } from '../../../_services/unit.service';
import { FoundationVehiclesService } from '../../../_services/vehicle.service';
import './vehicle-view.component.scss';

@UntilDestroy()
@Component({
    selector: 'vehicle-view',
    template: require('./vehicle-view.component.html')
})
export class VehicleViewComponent implements OnInit {
    vehicleId: number;
    vehicle: IVehicle;

    plots: IPlot[];
    journeys: IJourney[];

    sensorData: ISensorData[] = [];

    selectedVehicleCurrentUnit$: Observable<any | IUnit>;
    selectedVehiclePeripherals$: Observable<IViewCommissionPeripheral[]>;

    selectedPlot: IPlot;
    showSelectedPlotData: boolean;
    selectedMapPlot: IPlot;

    showingEditVehicleForm = false;

    SensorDataValueType = SensorDataValueType;

    locationMarker: IGoogleMapsMarker;
    resourceType: ResourceType = ResourceType.Vehicle;

    tableColumns: IVTableColumn<IPlot>[]
    tableRows: IVTableRow[];

    loading: boolean;
    diagnosticsInProcess: boolean;

    latestPulseRecord: IPulseRecord;

    private p100ProductId = 1253;

    @ViewChild(Diagnostics4gComponent) diagnostic4g: Diagnostics4gComponent;
    @ViewChild('vehicleEditor') userEditor: AddVehicleComponent;

    constructor(
        private readonly dateService: DateService,
        private readonly journeyService: JourneyService,
        private readonly mapService: MapService,
        private readonly navigationService: NavigationService,
        private readonly peripheralService: PeripheralService,
        private readonly plotService: PlotService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly unitService: UnitsService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly vTableService: VTableService,
        private readonly pulseService: PulseService
    ) { }

    ngOnInit(): void {
        this.vehicleId = this.navigationService.getRouteParam('vehicleId');
        this.loadData();
    }

    get shouldShowUpdatedTimePill(): boolean {
        return new Date(this.vehicle?.Updated).getTime() !== new Date('0001-01-01T00:00:00Z').getTime();
    }
    
    showEditVehicleForm(): void {
        this.showingEditVehicleForm = true;
    }

    onVehicleSaved(vehicle: IVehicle) {
        this.vehicle = vehicle;
        this.showingEditVehicleForm = false;
    }

    onEditCancelled() {
        this.showingEditVehicleForm = false;
    }

    onDiagnosticsInProcess(inProcess: boolean) {
        this.diagnosticsInProcess = inProcess;
    }

    onDiagnosticsClicked(peripheral: IViewCommissionPeripheral) {
        if (!peripheral.showDiagnostics) {
            peripheral.showDiagnostics = true;
        } else {
            this.diagnostic4g.refreshDiagnosticsTests();
        }
    }

    //--

    private loadData(): void {
        this.loading = true;

        combineLatest([
            this.plotService.getPlotsWithSensorData(this.vehicleId, this.dateService.getStartOfDay(new Date()), this.dateService.getEndOfDay(new Date())),
            this.vehicleService.getVehicle(this.vehicleId),
            from(this.journeyService.getJourneysForDateAndVehicle(this.vehicleId, this.dateService.getStartOfDay(new Date()), this.dateService.getEndOfDay(new Date()), true) as Promise<IJourney[]>),
            this.pulseService.getLatestPulseMessage({ VehicleId: this.vehicleId })
        ]).pipe(untilDestroyed(this)).subscribe({
            next: ([plots, vehicle, journeys, latestPulse]) => {
                this.vehicle = vehicle;
                this.plots = plots;
                this.latestPulseRecord = latestPulse;

                this.createTable();

                const latestPlot = this.getLatestPlot(plots);
                this.selectedMapPlot = latestPlot;
                this.journeys = journeys;

                this.locationMarker = latestPlot ? this.mapService.createMapMarker(latestPlot, { label: this.translationService.t('FoundationVehicles.CurrentLocation'), showLabel: true }) : null;

                this.getInstallerUnitAndMapping();
                this.updatePeripherals();

                this.createTableRows();
                this.loading = false;
            }
        })
    }

    private getInstallerUnitAndMapping() {
        this.selectedVehicleCurrentUnit$ = this.unitService.getVehicleCurrentUnit(this.vehicle);
    }

    private updatePeripherals(): void {
        this.selectedVehiclePeripherals$ = this.peripheralService.getInstalledPeripheralsForVehicle(this.vehicleId);
    }

    private getLatestPlot(plots: IPlot[]): IPlot {
        if (!plots.length) {
            return;
        }

        return plots.reduce((a, b) => {
            return new Date(a.Utc).getTime() > new Date(b.Utc).getTime() ? a : b;
        });
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.plots, this.tableColumns);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: plot => this.vTableService.createDateCell(plot.Utc)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reason", sortable: true, width: 120, minTableWidth: 600 }),
                createCellFunction: plot => this.vTableService.createTableCell(this.reasonCodeService.getReasonCodeLabel(plot.Reason))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Odometer", sortable: true, width: 1020, minTableWidth: 600, canEdit: true }),
                createCellFunction: plot => {
                    const value = this.unitConversionService.smartConvert((plot.Odometer / 1000), UnitConversionType.Distance, { precision: 1 });
                    return this.vTableService.createTableCell(value);
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Fuel", sortable: true, minTableWidth: 600, canEdit: true }),
                createCellFunction: plot => {
                    const value = this.unitConversionService.smartConvert(plot.SensorData?.FuelLevel, UnitConversionType.FuelCapacity, { precision: 1 });
                    return this.vTableService.createTableCell(value)
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Speed", sortable: true, minTableWidth: 600, canEdit: true }),
                createCellFunction: plot => {
                    const value = this.unitConversionService.smartConvert(plot.Velocity, UnitConversionType.Speed, { precision: 1 });
                    return this.vTableService.createTableCell(value)
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Direction", sortable: true, minTableWidth: 600, canEdit: true }),
                createCellFunction: plot => {
                    return this.vTableService.createTableCell(this.plotService.convertDirectionToWords(plot.Direction))
                }
            }
        ]

        this.getCanDataColumns();
    }

    private getCanDataColumns(): void {
        if (!this.plots?.length) {
            return;
        }

        const samplePlot = this.plots.find(plot => !!plot.SensorData);

        if (!samplePlot) {
            return;
        }

        const canDataColumns = Object.keys(samplePlot.SensorData) as (keyof ISensorData)[];
        const excludedColumns = ["PlotId", "VehicleId", "Utc"];

        canDataColumns.forEach(key => {
            if (excludedColumns.includes(key)) {
                return;
            }

            this.tableColumns.push({
                header: this.vTableService.createTableHeader({ tKey: 'SensorDataValueTypeEnum.' + key, sortable: false, canEdit: true, hidden: true }),
                createCellFunction: plot => {
                    let data: any;

                    if (plot.SensorData?.[key]) {
                        data = plot.SensorData[key];
                    }

                    return this.vTableService.createTableCell(data, {tooltip: true});
                }
            })
        });
    }
}
